import { type EntityDescriptor } from '../entities';
import {
  type ClauseValue,
  type ControlledFlag,
  type ControlledSegment,
  type FlagVariation,
} from '../types';

export const IN_OP = 'in';
export const SEGMENT_MATCH_OP = 'segmentMatch';

/**
 * Works out, for one flag and one entity, where that entity is (or could be)
 * targeted, and so which change a toggle can make.
 *
 * It reports targeting structure, not a served value: `resolveServedValue`
 * works that out. Anything it cannot read gives no handle.
 */

/** A place where the entity id can be added to or removed from targeting. */
export type TargetingHandle =
  | {
      type: 'flagClause';
      ruleId: string;
      clauseId: string;
      servesOn: boolean;
      present: boolean;
    }
  | {
      type: 'flagTarget';
      servesOn: boolean;
      present: boolean;
    }
  | {
      type: 'segmentClause';
      segmentKey: string;
      ruleId: string;
      clauseId: string;
      servesOn: boolean;
      present: boolean;
    }
  | {
      type: 'segmentTarget';
      segmentKey: string;
      servesOn: boolean;
      present: boolean;
    };

export interface FlagTargetingAnalysis {
  enabled: boolean;
  /** Whether everyone no rule matches gets the "on" value. */
  fallthroughIsOn: boolean;
  handles: TargetingHandle[];
}

export const variationIsOn = (
  variations: FlagVariation[],
  index: number | undefined
): boolean =>
  index !== undefined && index >= 0 && Boolean(variations[index]?.isOn);

const valuesInclude = (values: ClauseValue[], entityId: string): boolean =>
  values.some((value) => String(value) === entityId);

const segmentHandles = (
  segment: ControlledSegment,
  descriptor: EntityDescriptor,
  entityId: string,
  servesOn: boolean
): TargetingHandle[] => {
  if (segment.unbounded) return [];

  const handles: TargetingHandle[] = [];

  if (descriptor.isContextKey) {
    handles.push({
      type: 'segmentTarget',
      segmentKey: segment.key,
      servesOn,
      present: segment.included.includes(entityId),
    });
  }

  segment.rules.forEach((rule) => {
    const ruleId = rule.id;
    if (!ruleId) return;

    rule.clauses.forEach((clause) => {
      const clauseId = clause.id;
      if (
        !clauseId ||
        clause.attribute !== descriptor.clauseAttribute ||
        clause.op !== IN_OP ||
        clause.negate
      ) {
        return;
      }

      handles.push({
        type: 'segmentClause',
        segmentKey: segment.key,
        ruleId,
        clauseId,
        servesOn,
        present: valuesInclude(clause.values, entityId),
      });
    });
  });

  return handles;
};

const ruleHandles = (
  flag: ControlledFlag,
  descriptor: EntityDescriptor,
  entityId: string,
  segmentsByKey: Map<string, ControlledSegment>
): TargetingHandle[] => {
  const handles: TargetingHandle[] = [];

  flag.rules.forEach((rule) => {
    const ruleId = rule.id;
    // A paused rule serves nothing, so writing to it would change nothing.
    if (!ruleId || rule.disabled) return;

    // A rollout can serve either value, so it never counts as "on".
    const servesOn =
      !rule.isRollout && variationIsOn(flag.variations, rule.variation);

    rule.clauses.forEach((clause) => {
      if (clause.op === SEGMENT_MATCH_OP) {
        if (clause.negate) return;

        clause.values.forEach((value) => {
          const segment = segmentsByKey.get(String(value));
          if (segment) {
            handles.push(
              ...segmentHandles(segment, descriptor, entityId, servesOn)
            );
          }
        });
        return;
      }

      const clauseId = clause.id;
      if (
        !clauseId ||
        clause.attribute !== descriptor.clauseAttribute ||
        clause.op !== IN_OP ||
        clause.negate
      ) {
        return;
      }

      handles.push({
        type: 'flagClause',
        ruleId,
        clauseId,
        servesOn,
        present: valuesInclude(clause.values, entityId),
      });
    });
  });

  return handles;
};

const targetHandles = (
  flag: ControlledFlag,
  descriptor: EntityDescriptor,
  entityId: string
): TargetingHandle[] => {
  if (!descriptor.isContextKey) return [];

  return flag.targets
    .filter(
      (target) => (target.contextKind ?? 'user') === descriptor.contextKind
    )
    .map((target) => ({
      type: 'flagTarget' as const,
      servesOn: variationIsOn(flag.variations, target.variation),
      present: target.values.includes(entityId),
    }));
};

export interface AnalyzeTargetingParams {
  flag: ControlledFlag;
  descriptor: EntityDescriptor;
  entityId: string;
  segments: ControlledSegment[];
}

export const analyzeTargeting = ({
  flag,
  descriptor,
  entityId,
  segments,
}: AnalyzeTargetingParams): FlagTargetingAnalysis => {
  const segmentsByKey = new Map(
    segments.map((segment) => [segment.key, segment])
  );

  const handles = [
    ...targetHandles(flag, descriptor, entityId),
    ...ruleHandles(flag, descriptor, entityId, segmentsByKey),
  ];

  return {
    enabled: flag.enabled,
    fallthroughIsOn:
      !flag.fallthrough.isRollout &&
      variationIsOn(flag.variations, flag.fallthrough.variation),
    handles,
  };
};

/** Whether any rule references a group, so groups must be loaded to analyse it. */
export const usesSegments = (flag: ControlledFlag): boolean =>
  flag.rules.some((rule) =>
    rule.clauses.some((clause) => clause.op === SEGMENT_MATCH_OP)
  );

/** The keys of the groups the flags' rules use, each once, sorted. */
export const segmentKeysOf = (flags: ControlledFlag[]): string[] =>
  [
    ...new Set(
      flags.flatMap((flag) =>
        flag.rules.flatMap((rule) =>
          rule.clauses
            .filter((clause) => clause.op === SEGMENT_MATCH_OP)
            .flatMap((clause) => clause.values.map(String))
        )
      )
    ),
  ].sort();
